import React from 'react'
import { useNavigate } from 'react-router-dom'
import LecturesSubNav from '../components/LecturesSubNav'
import SharedFilters from '../../../components/SharedFilters'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer'
import SyllabusNotesModal from './components/SyllabusNotesModal'
import useSyllabusListScreen from './hooks/useSyllabusListScreen'

export default function SyllabusListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useSyllabusListScreen()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Top Header & SubNav Canopy ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Education Operations</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            List of Syllabus
          </h1>
        </div>
      </section>

      {/* ── Collapsible Advanced Filters (SharedFilters) ── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        courseFilter={state.courseFilter}
        branchFilter={state.branchFilter}
        schoolFilter={state.schoolFilter}
        academicYearFilter={state.academicYearFilter}
        subjectFilter={state.subjectFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setCourseFilter={actions.setCourseFilter}
        setBranchFilter={actions.setBranchFilter}
        setSchoolFilter={actions.setSchoolFilter}
        setAcademicYearFilter={actions.setAcademicYearFilter}
        setSubjectFilter={actions.setSubjectFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        onReset={actions.resetFilters}
        onSearch={() => { }}

        searchFieldOptions={state.options.searchFields}
        courseOptions={state.options.courses}
        branchOptions={state.options.branches}
        schoolOptions={state.options.schools}
        academicYearOptions={state.options.academicYears}
        subjectOptions={state.options.subjects}

        accordionFilters={['course', 'branch', 'school', 'academicYear', 'subject', 'fromDate', 'toDate']}

        tools={[
          { key: 'export-excel', label: 'Export to Excel', icon: 'file_download' },
          { key: 'export-pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
          { key: 'upload-syllabus', label: 'Upload Syllabus', icon: 'upload_file' },
          { key: 'print-report', label: 'Print Report', icon: 'print' },
        ]}
        onToolSelect={(toolKey) => {
          if (toolKey === 'add-new') {
            navigate('/lectures/syllabus/entry')
          } else if (toolKey === 'upload-syllabus') {
            navigate('/lectures/upload?tab=syllabus')
          } else if (toolKey === 'print-report') {
            actions.setIsReportsOpen(true)
          } else if (toolKey === 'export-excel') {
            actions.handleExport('XLSX')
          } else if (toolKey === 'export-pdf') {
            actions.handleExport('PDF')
          }
        }}
      />

      {/* ── Syllabus GridView Table ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-xs text-on-surface">
            <thead className="border-b border-outline-variant/25 bg-surface-container-low/70 text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              <tr>
                <th className="px-5 py-3.5">Course</th>
                <th className="px-5 py-3.5">Subject</th>
                <th className="w-16 px-4 py-3.5 text-center">No.</th>
                <th className="px-5 py-3.5">Chapter / Topic</th>
                <th className="px-5 py-3.5">Schedule (Start - End)</th>
                <th className="w-24 px-4 py-3.5 text-center">Hours</th>
                <th className="w-16 px-4 py-3.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {state.loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined animate-spin text-2xl text-primary">progress_activity</span>
                      <span className="text-xs font-semibold">Loading syllabus items...</span>
                    </div>
                  </td>
                </tr>
              ) : state.paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-14 text-center text-on-surface-variant">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-3xl text-outline">menu_book</span>
                      <span className="font-bold text-on-surface">No syllabus items found</span>
                      <span className="text-xs">Try adjusting your filters or click "Add New Syllabus" to create one.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                state.paginatedItems.map((item) => (
                  <tr
                    key={item.id}
                    onClick={() => navigate(`/lectures/syllabus/entry/${item.id}`)}
                    className="group cursor-pointer transition-colors hover:bg-surface-container-low/50"
                  >
                    {/* Course */}
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-1">
                        <span className="inline-flex self-start rounded-xl bg-primary/10 px-2 py-0.5 text-[11px] font-extrabold text-primary">
                          {item.course}
                        </span>
                        <span className="text-[10px] text-on-surface-variant">
                          AY: {item.academicYear}
                        </span>
                      </div>
                    </td>

                    {/* Subject */}
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-on-surface">{item.subject}</span>
                        {item.schoolCollege && item.schoolCollege !== 'All' && (
                          <span className="text-[10px] text-secondary font-medium flex items-center gap-1">
                            <span className="material-symbols-outlined text-xs">school</span>
                            {item.schoolCollege}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Chapter No. */}
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-surface-container-high text-xs font-black text-on-surface">
                        {item.chapterNo}
                      </span>
                    </td>

                    {/* Chapter / Topic */}
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-1">
                        <span className="font-bold text-on-surface text-xs">{item.chapterName}</span>
                        <div className="flex items-center gap-2">
                          {/* {item.hasNotes && (
                            <span
                              onClick={() => actions.setNotesModalItem(item)}
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded cursor-pointer hover:bg-emerald-500/20 transition"
                              title="Click to view/manage notes"
                            >
                              <span className="material-symbols-outlined text-[12px]">picture_as_pdf</span>
                              Notes
                            </span>
                          )}
                          {item.videoUrl && (
                            <a
                              href={item.videoUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] font-bold text-primary hover:underline"
                            >
                              <span className="material-symbols-outlined text-[12px]">videocam</span>
                              Video
                            </a>
                          )} */}
                        </div>
                      </div>
                    </td>

                    {/* Schedule */}
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-0.5 text-xs">
                        <div className="flex items-center gap-1.5 font-semibold text-on-surface">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Start:</span>
                          <span>{item.startDate || '—'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 font-medium text-on-surface-variant">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-outline">End:</span>
                          <span>{item.endDate || '—'}</span>
                        </div>
                      </div>
                    </td>

                    {/* Hours */}
                    <td className="px-4 py-3.5 text-center">
                      <span className="inline-flex items-center gap-1 rounded-xl bg-surface-container-high px-2 py-0.5 text-xs font-bold text-on-surface">
                        <span className="material-symbols-outlined text-xs text-primary">schedule</span>
                        {item.hours} hrs
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end">
                        <AppActionMenu
                          triggerLabel=""
                          triggerIcon="more_vert"
                          showChevron={false}
                          triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                          align="right"
                          items={[
                            { key: 'edit', label: 'Edit', icon: 'edit' },
                            { key: 'notes', label: 'Upload Notes', icon: 'description' },
                            { key: 'delete', label: 'Delete', icon: 'delete', danger: true },
                          ]}
                          onSelect={(actionKey) => {
                            if (actionKey === 'edit') {
                              navigate(`/lectures/syllabus/entry/${item.id}`)
                            } else if (actionKey === 'notes') {
                              actions.setNotesModalItem(item)
                            } else if (actionKey === 'delete') {
                              actions.handleDelete(item.id, item.chapterName)
                            }
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination */}
        <AppPagination
          currentPage={state.page}
          totalPages={state.totalPages}
          totalItems={state.totalCount}
          pageSize={state.pageSize}
          onPageChange={actions.setPage}
          onPageSizeChange={(newSize) => {
            actions.setPageSize(newSize)
            actions.setPage(1)
          }}
        />
      </section>

      {/* ── Global Predefined Reports Drawer ── */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsOpen}
        onClose={() => actions.setIsReportsOpen(false)}
        title="Syllabus Reports"
        module="education"
        submodule="syllabus"
        moduleLabel="Syllabus Curriculum"
        subtitle="Curriculum Delivery & Completion Analytics"
        filters={{
          academicYear: state.academicYearFilter,
          courseId: state.courseFilter,
          subjectId: state.subjectFilter,
          schoolId: state.schoolFilter,
          fromDate: state.fromDate,
          toDate: state.toDate,
          searchField: state.searchField,
          searchText: state.searchText,
        }}
      />

      {/* ── Notes Upload Modal ── */}
      <SyllabusNotesModal
        isOpen={Boolean(state.notesModalItem)}
        item={state.notesModalItem}
        onClose={() => actions.setNotesModalItem(null)}
        onSaveNotes={actions.handleSaveNotes}
      />
    </div>
  )
}
